/**
 * English locale — the single source of user-facing copy for launch.
 *
 * Adding a locale (Turkish is the planned second pass) means adding a sibling file with the same
 * keys and registering it in `./index.ts`; no component changes.
 */
export const en = {
  'app.name': 'NightBand',
  'app.tagline': 'Tune the dead air. Something answers.',

  'landing.lede':
    'A receiver, a band of empty frequencies, and a night that is longer than it should be. Turn the dial slowly. Most of what you find is noise. Not all of it.',
  'landing.point.listen.title': 'Listen',
  'landing.point.listen.body':
    'Everything is audio. Static, interference, half-caught voices bleeding across the band.',
  'landing.point.tune.title': 'Tune',
  'landing.point.tune.body':
    'Signals live at exact frequencies at exact hours. Find them before they move.',
  'landing.point.answer.title': 'Answer',
  'landing.point.answer.body':
    'Some transmissions are waiting for a reply. You will not always know what is on the other end.',
  'landing.enter': 'Sign in with Google',
  'landing.enterHint': 'Google Sign-In · minimum age 16 · headphones strongly recommended',
  'landing.continue': 'Enter the band',
  'landing.audioPrompt': 'Click anywhere to enable audio',
  'landing.audioOn': 'Ambient carrier: live',
  'landing.audioOff': 'Ambient carrier: muted',
  'landing.unconfigured': 'Sign-in is not configured on this server yet.',

  // ---- Landing marketing surface ("Broadcast" visual upgrade) ----
  // Copy and voice lifted from the store description; the store page is the reference for both the
  // aesthetic and the wording.
  'landing.heroTagline':
    'Somewhere on the dial, someone is still transmitting. Find them before the static does.',
  'landing.playWeb': 'Play free in your browser',

  'landing.studio.role': 'Developer & Publisher',
  'landing.studio.name': 'GADDAR',
  'landing.studio.web': 'radio.deshretmaat.com',

  'landing.tags.label': 'Tags',
  'landing.tag.horror': 'Horror',
  'landing.tag.mystery': 'Mystery',
  'landing.tag.atmospheric': 'Atmospheric',
  'landing.tag.audio': 'Audio-Based',
  'landing.tag.investigation': 'Investigation',
  'landing.tag.voice': 'Voice Control',
  'landing.tag.singleplayer': 'Singleplayer',
  'landing.tag.presence': 'Online Co-op Presence',
  'landing.tag.psychological': 'Psychological',

  'landing.pullquote':
    "You don't play NightBand with your eyes. You play it with your ears — leaning in, holding your breath, sure that if you just tune a hair closer you'll finally make out the words.",

  'landing.about.title': 'About this game',
  'landing.about.p1':
    'NightBand is a sound-first horror mystery. There is no map and no world to walk through — only a radio receiver and a long night. You turn a dial across a band of dead frequencies, push through the static, and find transmissions that surface at exact frequencies at exact hours.',
  'landing.about.p2':
    'You listen. You hold a signal until it settles out of the noise, piece together who is on the other end, and answer them over the air with your own voice. Everything you learn is written down for you; the pattern is in what you have already heard.',
  'landing.about.p3':
    'Some of what you find is a recording. Some of it is a character. Some of it might be another player, passing through the same frequency at the same moment. On NightBand you are never quite sure which — and that is the point.',

  'landing.features.title': 'Key features',
  'landing.feature.tune.title': 'A radio you actually tune',
  'landing.feature.tune.body':
    'A continuous shortwave band, not a menu of stations. Sweep it by hand; a signal fades up out of the static as you close on it, and slips back into noise the moment you drift.',
  'landing.feature.sound.title': 'Sound is the whole game',
  'landing.feature.sound.body':
    'No cutscenes, no HUD to read. The mystery lives entirely in what you can hear — filtered voices, interference, the texture of a channel that should be empty.',
  'landing.feature.speak.title': 'Speak into the dark',
  'landing.feature.speak.body':
    'Hold the key and answer out loud. The people you find on the band respond to what you say — and if the mic mishears, the written replies are always there.',
  'landing.feature.solve.title': 'A mystery you solve by listening',
  'landing.feature.solve.body':
    'No quest markers. You learn the truth the way an operator would: by holding frequencies, reading back your own log, and noticing what does not add up.',
  'landing.feature.ghosts.title': 'Ghost signals — other players, passing through',
  'landing.feature.ghosts.body':
    'Fragments of other people drift across the band in real time. Never a lobby, never a chat — just the sense that you are not the only one awake tonight.',
  'landing.feature.roam.title': 'Voices that roam the band',
  'landing.feature.roam.body':
    'Some transmitters do not sit still. Learn their patterns, catch them while they are keyed up, and decide what to tell them.',

  'landing.midband.q': 'Is it a recording? A character? A real person? Or something else?',
  'landing.midband.a': "On NightBand, you're never quite sure — and that's the point.",

  'landing.info.perspective.label': 'Perspective',
  'landing.info.perspective.value': 'First-person · audio-first',
  'landing.info.play.label': 'Play style',
  'landing.info.play.value': 'Solo story + ambient online presence',
  'landing.info.enjoyed.label': 'Best enjoyed',
  'landing.info.enjoyed.value': 'Dark room · headphones · alone',

  'landing.bullet.saves': 'Cloud saves — close the tab, resume from anywhere.',
  'landing.bullet.access': 'Accessibility built in — captions, voice clarity, reduced-motion.',

  'landing.footer.tagline': 'An independently developed audio horror mystery.',

  'consent.title': 'Before you transmit',
  'consent.lede':
    'NightBand records who agreed to what, and when. Read both documents. You cannot enter without agreeing — and there is no other way in.',
  'consent.tab.eula': 'Terms of Service',
  'consent.tab.privacy': 'Privacy Policy',
  'consent.version': 'Version {version} · effective {date}',
  'consent.scrollHint': 'Scroll to the end of both documents to continue.',
  'consent.checkbox':
    'I have read and agree to the Terms of Service and the Privacy Policy, and I am at least 16 years old.',
  'consent.agree': 'I agree — enter',
  'consent.decline': 'Decline and sign out',
  'consent.pending': 'Recording consent…',
  'consent.error': 'Consent could not be recorded. Try again.',
  'consent.versionChanged': 'These documents were updated. Please review the current version.',
  'consent.openFull': 'Open in a new tab',

  // ---- Main Menu ----
  'menu.title': 'Main Menu',
  'menu.subtitle': 'The set is warm. Choose where to begin.',
  'menu.continue': 'Continue',
  'menu.continueHint': 'Resume where you left off',
  'menu.newGame': 'New Game',
  'menu.newGameHint': 'Start a fresh night',
  'menu.confirmNewGame': 'Start a new game? Your current unsaved progress will be lost.',
  'menu.load': 'Load Save',
  'menu.loadHint': 'Open one of your four saves',
  'menu.settings': 'Settings',
  'menu.settingsHint': 'Audio and accessibility',
  'menu.logOut': 'Log Out',
  'menu.logOutHint': 'End the session and sign out',
  'menu.back': 'Back',
  'menu.play': 'Enter the band',

  'menu.saves.title': 'Saves',
  'menu.saves.lede': 'Four slots. Load one back whenever — save the night from inside the game.',
  'menu.saves.loading': 'Reading slots…',
  'menu.saves.error': 'Could not reach your saves. Try again.',
  'menu.slot.title': 'Slot {n}',
  'menu.slot.empty': 'Empty',
  'menu.slot.clues': '{held}/{total} clues',
  'menu.slot.resolved': 'Resolved',
  'menu.slot.saved': 'Saved {date}',
  'menu.slot.load': 'Load',
  'menu.slot.save': 'Save here',
  'menu.slot.delete': 'Clear',
  'menu.slot.confirmDelete': 'Clear slot {n}?',
  'menu.slot.confirmYes': 'Yes',
  'menu.slot.confirmNo': 'Cancel',
  'menu.slot.saving': 'Saving…',
  'menu.slot.loading': 'Loading…',
  'menu.slot.saveError': 'Save failed. Try again.',
  'menu.slot.loadError': 'Load failed. Try again.',

  'menu.settings.title': 'Settings',
  'menu.settings.lede': 'These ride your account — they follow you to any device.',

  'saves.confirmLoad': 'Load this save? Your current unsaved progress will be lost.',

  'radio.title': 'Receiver',
  'radio.status.online': 'Receiver online',
  'radio.status.offline': 'Receiver offline',
  'radio.frequency': 'Frequency',
  'radio.signal': 'Signal',
  'radio.noise': 'Noise floor',
  'radio.noise.worklet': 'Noise floor: synthesised (worklet)',
  'radio.noise.buffer': 'Noise floor: synthesised (buffered fallback)',
  'radio.status.standby': 'Receiver on standby — power on to listen',
  'radio.clock': 'Night {day} · {time}',
  'radio.clock.label': 'World time',
  'radio.receiving': 'Receiving: {label}',
  'radio.deadAir': 'Dead air',
  'radio.band': 'Band {min}–{max} kHz',
  'radio.packFailed':
    'The world could not be loaded. Tuning still works; there is nothing on the air.',
  'radio.power': 'Power',
  'radio.powerOn': 'Power on',
  'radio.powerOff': 'Power off',
  'radio.volume': 'Volume',
  'radio.saved': 'Synced',
  'radio.saving': 'Syncing…',
  'radio.savedOffline': 'Offline — cached locally',
  'radio.abandon': 'Abandon',
  'radio.confirmAbandon': 'Abandon this game? Unsaved progress will be lost.',
  'radio.save': 'Save',
  'radio.saveMenu.title': 'Save to a slot',
  'radio.saveMenu.lede': 'Four slots. Save into an empty one; load or clear the ones you have.',
  'radio.saveMenu.close': 'Close',
  // The front panel carries instruments only; the audio and accessibility switches live behind
  // the gear in the header, the way a set's service settings live behind its case.
  'radio.settings': 'Settings',
  'radio.settings.title': 'Receiver settings',
  'radio.settings.close': 'Close',
  'radio.settingsHint': 'Audio and accessibility. These ride your account.',
  'radio.ghosts': 'Ghost signals',
  'radio.ghosts.on': 'Audible',
  'radio.ghosts.off': 'Muted',
  'radio.effects': 'Effects',
  'radio.effects.full': 'Full',
  'radio.effects.reduced': 'Reduced',
  'radio.effects.off': 'Off',

  'investigation.title': 'Case notes',
  'investigation.tab.log': 'Log',
  'investigation.tab.clues': 'Clues',
  'investigation.tab.truth': 'Truth',
  'investigation.open': 'Open case notes',
  'investigation.close': 'Close',

  'log.empty': 'Nothing logged yet. Hold a frequency until something resolves out of the noise.',
  'log.heading': 'Frequencies where something answered',
  'log.entry.time': 'Night {day} · {time}',
  'log.note.placeholder': 'Note…',
  'log.note.label': 'Note for {khz} kHz',
  'log.tune': 'Tune to {khz} kHz',

  'clues.empty': 'No clues yet.',
  'clues.count': '{held} of {total}',

  'truth.premise': 'The case',
  'truth.threads': 'What you have',
  'truth.thread': '{label}: {held} of {total}',
  'truth.unthreaded': 'Loose ends',
  'truth.unresolved': 'Not enough yet.',
  'truth.resolved': 'Resolved',

  'dialogue.title': 'Transmission',
  'dialogue.transmit': 'Answer',
  'dialogue.leave': 'Stop transmitting',
  'dialogue.ended': 'The channel goes quiet.',
  'dialogue.noReply': 'Nothing to say.',
  'dialogue.inputHint':
    'Speak by holding the transmit key, or click a reply — or press its number. All three reach them the same.',
  // The operator's card (Chapter 7). Written by whoever left the set: terse, procedural, and
  // giving away slightly more than it means to. Instructions first; the last line is the one
  // that turns a manual into an artefact.
  // Captions (Chapter 7). The receiving line says *who*; a caption says what it sounds like,
  // which is the half a player who cannot hear is otherwise missing entirely. One per preset, so
  // a new parasite signal is captioned the moment an author adds it.
  'caption.title': 'Captions',
  'caption.static': 'Static. Nothing is transmitting here.',
  'caption.signal': '{label} — {sound}',
  'caption.signalUnknown': 'A signal — {sound}',
  'caption.preset.hum': 'a flat carrier tone, unbroken',
  'caption.preset.numbers': 'a voice reading numbers, evenly, without pause',
  'caption.preset.beacon': 'a single tone, repeating on a slow cycle',
  'caption.preset.morse': 'morse, slow and repeating',
  'caption.preset.voice-carrier': 'someone speaking, too faint to make out',
  'caption.preset.whisper': 'breath with a pitch in it, coming and going',
  'caption.preset.buzzer': 'a harsh buzz, switching on and off about once a second',
  'caption.preset.databurst': 'machine chatter in bursts — two tones, fast',
  'caption.preset.woodpecker': 'a hard pulsing knock, sweeping slowly across the band',
  'caption.preset.timesignal': 'ticks, one a second, with a longer tone every tenth',
  'caption.preset.machinery': 'a low turning rumble, almost below hearing',
  'caption.preset.voice': 'a recorded voice',
  'caption.ghost': 'A fragment of someone else, briefly.',
  'caption.off': 'The set is off.',
  'radio.captions': 'Captions',
  'radio.captions.on': 'On',
  'radio.captions.off': 'Off',
  'radio.clarity': 'Voice clarity',
  'radio.atmospherics': 'Interference',

  'toast.noAnswer': 'Nothing is answering on this frequency.',

  'card.title': 'Taped to the set',
  'card.dismiss': 'Turn it over',
  'card.tune':
    'Tuning is stiff. Drag it, or use the arrow keys — hold Shift for coarse. The band runs 3000 to 8000. Most of it is empty. Most of it always was.',
  'card.hold':
    'Do not sweep. A signal will not announce itself; sit on it until it settles and the set will log it for you.',
  'card.log':
    'Everything you hold long enough goes in the log. Open the case notes and read it back — the pattern is in what you have already heard.',
  'card.answer':
    'If something answers, you may answer it. Hold the transmit key and speak, or use the written replies. Both reach them the same.',
  'card.done':
    'That is everything the set does. The rest of it is listening, and I was never any good at knowing when to stop.',

  'dialogue.movedOn': 'Signal lost — {label}.',
  'dialogue.tunedAway': 'You tuned away — {label}.',

  'transmit.hold': 'Hold to transmit',
  // The key on the set is engraved with one word; the card beside it says to hold it.
  'transmit.label': 'Transmit',
  'transmit.transmitting': 'Transmitting…',
  'transmit.matching': 'Reading you…',
  'transmit.cooling': 'Set cooling · {seconds}s',
  'transmit.overheated': 'Transmitter hot · {seconds}s',
  'transmit.tooSoon': 'The set will not key again yet.',
  'transmit.heard': '“{text}”',
  'transmit.nothingHeard': 'Nothing came through. Speak or choose below.',
  'transmit.unmatched': 'That did not get through. Say it another way, or choose below.',
  'transmit.offline': 'The link is down. Choose below.',
  'transmit.cutOff': 'You held the key too long.',
  'transmit.error.permission-denied': 'No microphone permission. Choose below.',
  'transmit.error.no-microphone': 'No microphone found. Choose below.',
  'transmit.error.network': 'Speech recognition is offline. Choose below.',
  'transmit.error.unsupported': 'This browser cannot listen. Choose below.',
  'transmit.error.no-speech': 'Nothing came through. Speak or choose below.',
  'transmit.error.aborted': 'Transmission cut. Choose below.',
  'transmit.error.unknown': 'The microphone failed. Choose below.',
  'transmit.emptyChannel': 'Dead air. No response.',
  'transmit.hail': 'Hail station',
  'transmit.channelStandby': 'Channel on standby · Key mic to hail',
  'transmit.hailing': 'Hailing station…',

  'toast.clue': 'Clue recorded: {label}',
  'toast.resolved': 'You have enough. Open the case notes.',

  'error.title': 'Signal lost',
  'error.generic': 'Something broke in the chain. Reload and try again.',
  'error.auth_unconfigured': 'Google Sign-In is not configured on this server yet.',
  'error.auth_state_missing': 'Your sign-in attempt expired. Try again.',
  'error.auth_state_mismatch': 'Your sign-in attempt could not be verified. Try again.',
  'error.auth_code_missing': 'Google did not return an authorization code. Try again.',
  'error.account_banned': 'This account has been suspended.',
  'error.access_denied': 'Sign-in was cancelled.',
  'error.retry': 'Try again',
  'error.offline': 'Server unreachable. Working from the local cache.',

  'nav.terms': 'Terms',
  'nav.privacy': 'Privacy',
  'nav.contact': 'Contact',

  'a11y.dial': 'Tuning dial',
  'a11y.knob.frequency': 'Tuning knob',
  'a11y.power': 'Power',
  'a11y.switch.on': 'On',
  'a11y.switch.off': 'Off',
  'radio.dialHint': 'Drag to sweep · shift-drag to sweep fast · scroll or ←/→ to fine-tune',
  'a11y.skipToContent': 'Skip to content',
} as const;

export type StringKey = keyof typeof en;
